import defaultTheme from './configs/moko-default-theme.json';
import microsoftTheme from './configs/microsoft-theme.json';
import acmeTheme from './configs/acme-theme.json';
import type {
  BrandRamp,
  MokoTheme,
  MokoThemeConfig,
  ThemeColors,
} from './types';

export const TENANT_THEME_KEY = 'theme';
export const DEFAULT_THEME_KEY = 'QT';

export const APP_THEMES: Record<string, MokoThemeConfig> = {
  MICROSOFT: microsoftTheme as MokoThemeConfig,
  QT: defaultTheme as MokoThemeConfig,
  ACME: acmeTheme as MokoThemeConfig,
};

export const THEME_OPTIONS = Object.entries(APP_THEMES).map(
  ([key, config]) => ({
    key,
    label: config.label ?? config.name,
  })
);

export const ACTIVE_THEME: MokoThemeConfig = APP_THEMES[DEFAULT_THEME_KEY]!;

const resolveThemeConfig = (key?: string | null): MokoThemeConfig =>
  (key && APP_THEMES[key]) || ACTIVE_THEME;

/**
 * Mirrors `@moko/utils` `resolveOtherInfoObj` so this package stays free of
 * web-only utils barrel imports (import.meta) that break React Native tests.
 */
const resolveOtherInfoObj = (
  otherInfo: unknown
): Record<string, unknown> | null => {
  if (otherInfo == null) {
    return null;
  }
  if (typeof otherInfo === 'object') {
    return otherInfo as Record<string, unknown>;
  }
  if (typeof otherInfo !== 'string') {
    return null;
  }
  try {
    const parsed = JSON.parse(otherInfo) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, unknown>;
    }
    if (typeof parsed === 'string') {
      const nested = JSON.parse(parsed) as unknown;
      return nested && typeof nested === 'object'
        ? (nested as Record<string, unknown>)
        : null;
    }
  } catch {
    return null;
  }
  return null;
};

export const themeConfigFromOtherInfo = (
  otherInfo: unknown
): MokoThemeConfig => {
  const value = resolveOtherInfoObj(otherInfo)?.[TENANT_THEME_KEY];
  return typeof value === 'string' ? resolveThemeConfig(value) : ACTIVE_THEME;
};

export const brandSemanticColors = (
  brand?: BrandRamp
): Partial<ThemeColors> => {
  if (!brand) {
    return {};
  }

  const primary = brand['80'];
  const primaryDark = brand['70'];
  const avatar = brand['150'];
  const selected = brand['160'];

  return {
    ...(primary
      ? {
          primary,
          buttonPrimary: primary,
          buttonSecondary: primary,
          badgeBlue: primary,
          badgeInfoText: primary,
          link: primary,
        }
      : {}),
    ...(primaryDark ? { primaryDark, linkDark: primaryDark } : {}),
    ...(avatar ? { backgroundAvatar: avatar, rangeFill: avatar } : {}),
    ...(selected
      ? {
          backgroundSelected: selected,
          backgroundLookupSelected: selected,
          sectionHighlight: selected,
          badgeInfoBg: selected,
        }
      : {}),
  };
};

export const buildMokoTheme = (config: MokoThemeConfig): MokoTheme => {
  const defaults = ACTIVE_THEME;
  const baseFont = defaults.font;
  const baseColors = defaults.colors;

  if (!baseFont?.nativeWeights || !baseColors) {
    throw new Error('The default theme is missing required fonts or colors');
  }

  const font = config.font ?? baseFont;
  const colors = {
    ...baseColors,
    ...brandSemanticColors(config.brand),
    ...config.colors,
  } as ThemeColors;

  return {
    name: config.name,
    fontFamily: font.family,
    fonts: font.nativeWeights,
    colors,
  };
};
